import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Sparkle} from '../../icons';
import {isOk} from '../../../utils/apiResponse';
import {AlertRulesApi} from '../../../api/alertRulesApi';
import {
    canConvert,
    clarifyKey,
    draftFromNaturalLanguage,
    MARK_ASK,

    NL_MAX_LENGTH,
    nlErrorKey,
    scopeChoices,
    visibleNotes,
} from '../naturalLanguageModel';

/**
 * «Crear una alerta › En tus palabras» (Ola 3, frente F4): la frase → el borrador del constructor.
 *
 * - No crea nada: arma el MISMO borrador que el modo paso a paso y lo entrega por `onDraft`; el
 *   constructor lo muestra con los chips interpretados resaltados y los dudosos vacíos, y «Simular» y
 *   «Guardar» son los de siempre.
 * - Sin el módulo Dashbo MCP no hay textarea: la nota y el modo guiado.
 * - Si no se pudo interpretar (IA caída o lenta), lo dice y ofrece armarla paso a paso.
 *
 * `marks` son las del borrador ACTUAL (el constructor las limpia a medida que el usuario corrige), así
 * la lista de lo que falta se achica sola.
 */
export function NaturalLanguageDraft({hasModule, canEdit = true, clientId = null, marks = {}, onDraft, onManual,
                                         onChooseScope, onTextChange = null}) {
    const {t, i18n} = useTranslation();
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorKey, setErrorKey] = useState(null);
    const [interpretedAs, setInterpretedAs] = useState(null);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    if (!hasModule || errorKey === 'alerts_center.nl.no_module') {
        return <p className="alc-notice" data-testid="alc-nl-note">{t('alerts_center.nl.no_module')}</p>;
    }

    const convert = (event) => {
        if (event) event.preventDefault();
        if (!canConvert(text) || loading || !canEdit) return;
        setLoading(true);
        setErrorKey(null);
        AlertRulesApi.draftFromText(text.trim(), clientId).then((response) => {
            if (!mounted.current) return;
            setLoading(false);
            if (!isOk(response) || !response.result) {
                setErrorKey(nlErrorKey(response));
                return;
            }
            const {draft, marks: nextMarks} = draftFromNaturalLanguage(response.result, {locale: i18n.language});
            setInterpretedAs(Array.isArray(response.result.interpreted_as) ? response.result.interpreted_as : []);
            onDraft(draft, nextMarks);
        });
    };

    const asks = Object.entries(marks).filter(([, mark]) => mark.kind === MARK_ASK);
    const choices = scopeChoices(marks);
    const notes = visibleNotes(interpretedAs, marks, i18n.language);
    const tooLong = text.length > NL_MAX_LENGTH;

    return (
        <div className="alc-nl" data-testid="alc-nl">
            <form className="alc-nl__form" onSubmit={convert}>
                <label htmlFor="alc-nl-text" className="alc-nl__label">{t('alerts_center.nl.label')}</label>
                <textarea id="alc-nl-text" className="alc-nl__text" rows={3} value={text}
                          placeholder={t('alerts_center.nl.placeholder')} disabled={!canEdit}
                          aria-invalid={tooLong ? 'true' : 'false'}
                          onChange={(event) => {
                              setText(event.target.value);
                              // El constructor lo cuenta como cambio: Escape y «Cancelar» preguntan antes de tirarlo.
                              if (onTextChange) onTextChange(event.target.value);
                          }}
                          onKeyDown={(event) => {
                              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) convert(event);
                          }}/>
                <div className="alc-nl__row">
                    <span className="alc-hint">{tooLong ? t('alerts_center.nl.text_length') : t('alerts_center.nl.hint')}</span>
                    <button type="submit" className="alc-btn -primary" data-testid="alc-nl-convert"
                            disabled={!canConvert(text) || loading || !canEdit}>
                        <Sparkle size={13} weight="bold" aria-hidden="true"/>
                        {loading ? t('alerts_center.nl.converting') : t('alerts_center.nl.convert')}
                    </button>
                </div>
            </form>

            {errorKey && (
                <div className="alc-notice -warn" role="alert" data-testid="alc-nl-error">
                    <span>{t(errorKey)}</span>
                    <button type="button" className="alc-link -button" onClick={onManual}>{t('alerts_center.nl.go_manual')}</button>
                </div>
            )}

            {interpretedAs && !errorKey && (
                <div className="alc-nl__understood" role="status" data-testid="alc-nl-understood">
                    <div className="alc-nl__understood-head">
                        <span className="alc-badge -ai">
                            <Sparkle size={11} weight="fill" aria-hidden="true"/>{t('alerts_center.nl.draft_badge')}
                        </span>
                        <button type="button" className="alc-link -button" onClick={onManual} data-testid="alc-nl-manual">
                            {t('alerts_center.nl.edit_manually')}
                        </button>
                    </div>
                    <p className="alc-nl__understood-text">{t('alerts_center.nl.understood')}</p>
                    {notes.length > 0 && (
                        <ul className="alc-nl__interpreted" data-testid="alc-nl-interpreted">
                            {notes.map((note, index) => <li key={index}>{note}</li>)}
                        </ul>
                    )}
                    {asks.length > 0 && (
                        <ul className="alc-nl__asks" data-testid="alc-nl-asks">
                            {asks.map(([field, mark]) => (
                                <li key={field} data-field={field}>
                                    {t(clarifyKey(mark.reason), {said: mark.said || '', interpolation: {escapeValue: false}})}
                                </li>
                            ))}
                        </ul>
                    )}
                    {choices.length > 0 && (
                        <div className="alc-nl__choices" data-testid="alc-nl-choices" role="group"
                             aria-labelledby="alc-nl-which-one">
                            <span className="alc-hint" id="alc-nl-which-one">{t('alerts_center.nl.which_one')}</span>
                            {choices.map((option) => (
                                <button key={option.id} type="button" className="alc-btn -ghost -small"
                                        onClick={() => onChooseScope(option.id)}>{option.name}</button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


